import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import projectService from "../../services/api/projects-service";
import { useHistory, useLocation } from "react-router-dom";
import UserContext from "../../UserContext";
import generalHelper from '../../variables/general-helper.js';
import Pagination from "react-js-pagination";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  CardText,
  Button,
  Input,
  Badge,
  Alert,
  Spinner,
  FormGroup,
  Label,
  CustomInput,
  Form,
} from "reactstrap"
import { InputGroup, InputGroupAddon } from "reactstrap";
import {
  AlertCircle,
  ArrowLeft,
  Search,
  Link2,
  Unlink,
  Check,
  X,
  Filter,
  Loader,
} from "lucide-react"
import Select from "react-select";
import Moment from "react-moment";
import ReactBSAlert from "react-bootstrap-sweetalert";

export default function ProjectLinkingDashboard() {
  // URL Parameters
  const projectIdFromUrl = useRef(null);
  const publisherIdFromUrl = useRef(null);

  // Navigation
  const history = useHistory();
  const location = useLocation();
  const context = useContext(UserContext);

  // Loading States
  const [isLoadingProject, setIsLoadingProject] = useState(true);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isUpdatingLinkStatus, setIsUpdatingLinkStatus] = useState(false);
  const [isLinkingProjects, setIsLinkingProjects] = useState(false);

  // Data States
  const [currentProjectDetails, setCurrentProjectDetails] = useState(null);
  const [projects, setProjects] = useState([]);
  const [linkedProjects, setLinkedProjects] = useState([]);
  const [allPublishers, setAllPublishers] = useState([]);
  const [totalProjects, setTotalProjects] = useState(0);

  // UI States
  const [activePage, setActivePage] = useState(1);
  const [linkedActivePage, setLinkedActivePage] = useState(1);
  const [linkedProjectSearch, setLinkedProjectSearch] = useState("");
  const [showPopup, setShowPopup] = useState({ alert: null });
  const [error, setError] = useState(null);

  // Form States
  const [formdetails, setFormDetails] = useState({
    publisherids: [],
    projectname: "",
    projectslug: "",
  });
  const [search, setSearch] = useState({});

  const records = 10;
  const linkedRecords = 10; // Pagination for linked projects

  // Initialize URL parameters and filters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    projectIdFromUrl.current = queryParams.get("project_id");
    publisherIdFromUrl.current = queryParams.get("publisher_id");

    if (!projectIdFromUrl.current) {
      setError("Project ID is required");
      return;
    }

    // Initialize form details with URL parameters
    let currentFormDetails = {
      publisherids: publisherIdFromUrl.current ? [publisherIdFromUrl.current] : [],
      projectname: "",
      projectslug: ""
    };

    // Parse filters from URL
    let filtersJSONString = generalHelper.getValueFromQueryString("filters");
    if (filtersJSONString) {
      try {
        const parsedFilters = JSON.parse(filtersJSONString);
        currentFormDetails = {
          ...currentFormDetails,
          ...parsedFilters,
          publisherids: parsedFilters.publisherids || currentFormDetails.publisherids,
        };
      } catch (error) {
        console.error("Error parsing filters from URL:", error);
      }
    }

    setFormDetails(currentFormDetails);
    setSearch(currentFormDetails);

    // Set page number
    let pageNumber = queryParams.get("page");
    if (pageNumber) {
      setActivePage(parseInt(pageNumber, 10));
    } else {
      setActivePage(1);
      generalHelper.addKeyToQueryString("page", 1);
    }
  }, [location.search]);

  // Fetch current project details
  useEffect(() => {
    const fetchCurrentProjectDetails = async () => {
      if (!projectIdFromUrl.current) return;

      setIsLoadingProject(true);
      setError(null);

      try {
        const projectDetails = await projectService.getLinkedProjectDetails(projectIdFromUrl.current);
        if (projectDetails.status) {
          const details = projectDetails.data;
          setCurrentProjectDetails(details);
          const sortedLinkedProjects = (details.linked_project_ids || []).sort(
            (a, b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at)
          );
          setLinkedProjects(sortedLinkedProjects);

          if (details.publishers_id && details.publishers_id.name) {
            const publisherOption = { value: details.publishers_id._id, label: details.publishers_id.name };
            setAllPublishers([publisherOption]);
          }
        } else {
          setError("Failed to fetch project details");
          setTimeout(() => history.replace("/projects"), 2000);
        }
      } catch (error) {
        console.error("Error fetching current project details:", error);
        setError("Error loading project details");
        setTimeout(() => history.replace("/projects"), 2000);
      } finally {
        setIsLoadingProject(false);
      }
    };

    fetchCurrentProjectDetails();
  }, [projectIdFromUrl.current, history]);

  // Fetch total projects count when filters or linked projects change
  useEffect(() => {
    const fetchTotalCount = async () => {
      if (!currentProjectDetails || !currentProjectDetails.enable_linking) {
        setTotalProjects(0);
        return;
      }

      try {
        const searchFilters = {
          ...search,
          publisherids: publisherIdFromUrl.current ? [publisherIdFromUrl.current] : search.publisherids,
        };

        const totalResponse = await projectService.getTotalProjects(searchFilters);

        if (totalResponse.status) {
          setTotalProjects(totalResponse.data.count);
        } else {
          setTotalProjects(0);
        }
      } catch (error) {
        console.error("Error fetching total projects:", error);
        setTotalProjects(0);
      }
    };

    fetchTotalCount();
  }, [search, currentProjectDetails]);

  // Fetch the list of available projects for the current page
  useEffect(() => {
    const fetchProjectsList = async () => {
      if (!currentProjectDetails || !currentProjectDetails.enable_linking) {
        setProjects([]);
        return;
      }

      setIsLoadingProjects(true);
      try {
        const searchFilters = {
          ...search,
          publisherids: publisherIdFromUrl.current ? [publisherIdFromUrl.current] : search.publisherids,
        };

        // This call fetches the projects for the current page
        const projectsData = await projectService.getAllProjects(
          searchFilters,
          activePage,
          records
        );

        if (projectsData.error?.errorCode === 403) {
          context.setIsAuthorized(false);
          history.replace("/dashboard/home");
          return;
        }

        if (projectsData.status && Array.isArray(projectsData.data)) {
          const sortedProjects = projectsData.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          const filteredProjects = sortedProjects.filter(
            (project) =>
              project.id !== projectIdFromUrl.current &&
              !linkedProjects.some((linkedProject) => linkedProject._id === project.id)
          );
          setProjects(filteredProjects);
        } else {
          setProjects([]);
        }
      } catch (error) {
        console.error("Error loading projects:", error);
        setProjects([]);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjectsList();
  }, [activePage, search, currentProjectDetails, context, history, publisherIdFromUrl.current]);

  // Event Handlers
  const handlePublisherChange = useCallback((value) => {
    const publisherIds = value ? value.map((publisherIdDetails) => publisherIdDetails.value) : [];
    setFormDetails(prev => ({
      ...prev,
      publisherids: publisherIds,
    }));
  }, []);

  const handleChange = useCallback((event) => {
    setFormDetails(prev => ({
      ...prev,
      [event.target.id]: event.target.value,
    }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    searchProjects();
  }, [formdetails]);

  const searchProjects = useCallback(() => {
    generalHelper.addKeyToQueryString("filters", JSON.stringify(formdetails));
    setActivePage(1);
    generalHelper.addKeyToQueryString("page", 1);
    setSearch(formdetails);
  }, [formdetails]);

  const resetElements = useCallback(() => {
    const defaultFormDetails = {
      publisherids: publisherIdFromUrl.current ? [publisherIdFromUrl.current] : [],
      projectname: '',
      projectslug: ''
    };

    setFormDetails(defaultFormDetails);
    generalHelper.addKeyToQueryString("filters", JSON.stringify(defaultFormDetails));
    generalHelper.addKeyToQueryString("page", 1);
    setSearch(defaultFormDetails);
    setActivePage(1);
  }, [publisherIdFromUrl.current]);

  const handlePageChange = useCallback((pageNumber) => {
    generalHelper.addKeyToQueryString("page", pageNumber);
    setActivePage(pageNumber);
  }, []);

  const handleLinkedPageChange = useCallback((pageNumber) => {
    setLinkedActivePage(pageNumber);
  }, []);

  const handleLinkedProjectSearchChange = useCallback((event) => {
    setLinkedProjectSearch(event.target.value);
    setLinkedActivePage(1); // Reset to first page on new search
  }, []);

  const handleClearLinkedProjectSearch = useCallback(() => {
    setLinkedProjectSearch("");
    setLinkedActivePage(1);
  }, []);

  const handleToggleLinking = async () => {
    if (!currentProjectDetails) return;

    setIsUpdatingLinkStatus(true);
    const newLinkingStatus = !currentProjectDetails.enable_linking;

    try {
      const response = await projectService.saveProjectSettings({
        projectid: projectIdFromUrl.current,
        enable_linking: newLinkingStatus,
      });

      if (response.status) {
        setCurrentProjectDetails(prev => ({
          ...prev,
          enable_linking: newLinkingStatus
        }));

        if (!newLinkingStatus) {
          setProjects([]);
          setTotalProjects(0);
        }
      } else {
        console.error("Failed to update linking status:", response.error);
        setError("Failed to update linking status");
      }
    } catch (error) {
      console.error("Error toggling linking status:", error);
      setError("Error updating linking status");
    } finally {
      setIsUpdatingLinkStatus(false);
    }
  };

  const handleLinkProject = async (projectIdToLink, projectName) => {
    showConfirmation(
      "Confirm Linking",
      `Are you sure you want to link "${projectName}" to ${currentProjectDetails.name}?`,
      async () => {
        setIsLinkingProjects(true);
        try {
          const response = await projectService.manageProjectLinks({
            project_id: projectIdFromUrl.current,
            project_ids: [projectIdToLink],
            action: "link",
          });

          if (response && response.status) {
            // Optimistically update state to avoid refetching
            const newlyLinkedProject = projects.find(p => p.id === projectIdToLink);
            if (newlyLinkedProject) {
              // Store the full project object to have all details (like slug) for unlinking.
              const projectToLink = { ...newlyLinkedProject, _id: newlyLinkedProject.id };
              delete projectToLink.id; // Use `_id` for consistency in the linked list.

              setLinkedProjects(prev => [projectToLink, ...prev]);
              setProjects(prev => prev.filter(p => p.id !== projectIdToLink));
              setTotalProjects(prev => prev - 1);
            }

            hideAlert();
          } else {
            console.error("Failed to link project:", response.error);
            setError("Failed to link project");
            hideAlert();
          }
        } catch (error) {
          console.error("Error linking project:", error);
          setError("Error linking project");
          hideAlert();
        } finally {
          setIsLinkingProjects(false);
        }
      }
    );
  };

  const handleUnlinkProject = async (projectId) => {
    showConfirmation(
      "Confirm Unlinking",
      `Are you sure you want to unlink this project from ${currentProjectDetails.name}?`,
      async () => {
        try {
          const response = await projectService.manageProjectLinks({
            project_id: projectIdFromUrl.current,
            project_ids: [projectId],
            action: "unlink"
          });

          if (response && response.status) {
            // Optimistically update state
            const unlinkedProject = linkedProjects.find(p => p._id === projectId);
            setLinkedProjects(prev => prev.filter(p => p._id !== projectId));

            if (unlinkedProject) {
              // Check if the unlinked project matches the current search filters.
              const matchesName = !search.projectname || (unlinkedProject.name && unlinkedProject.name.toLowerCase().includes(search.projectname.toLowerCase()));
              const matchesSlug = !search.projectslug || (unlinkedProject.slug && unlinkedProject.slug.toLowerCase().includes(search.projectslug.toLowerCase()));

              // If it matches filters and there's space on the current page, add it back to the list.
              if (matchesName && matchesSlug && projects.length < records) {
                const projectToAdd = { ...unlinkedProject, id: unlinkedProject._id };
                delete projectToAdd._id;
                setProjects(prev =>
                  [...prev, projectToAdd].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                );
              }
            }
            // The total count of available projects for pagination is always incremented.
            setTotalProjects(prev => prev + 1);
            hideAlert();
          } else {
            console.error("Failed to unlink project:", response.error);
            setError("Failed to unlink project");
            hideAlert();
          }
        } catch (error) {
          console.error("Error unlinking project:", error);
          setError("Error unlinking project");
          hideAlert();
        }
      }
    );
  };

  // Alert handlers
  const hideAlert = useCallback(() => {
    setShowPopup({ alert: null });
  }, []);

  const showConfirmation = useCallback((title, message, onConfirm) => {
    setShowPopup({
      alert: (
        <ReactBSAlert
          warning
          style={{ display: "block", marginTop: "-100px" }}
          title={title}
          onConfirm={onConfirm}
          onCancel={hideAlert}
          confirmBtnBsStyle="info"
          cancelBtnBsStyle="danger"
          confirmBtnText="Yes"
          cancelBtnText="Cancel"
          showCancel
        >
          {message}
        </ReactBSAlert>
      ),
    });
  }, [hideAlert]);

  // Calculate pagination info
  const firstRecord = projects.length > 0 ? (activePage - 1) * records + 1 : 0;
  const lastRecord = projects.length > 0 ? (activePage - 1) * records + projects.length : 0;
  const primaryColor = "#473FF2";

  // Pagination for linked projects
  const filteredLinkedProjects = linkedProjects.filter(project =>
    project?.name?.toLowerCase().includes(linkedProjectSearch?.toLowerCase())
  );

  const indexOfLastLinked = linkedActivePage * linkedRecords;
  const indexOfFirstLinked = indexOfLastLinked - linkedRecords;
  const currentLinkedProjects = filteredLinkedProjects.slice(indexOfFirstLinked, indexOfLastLinked);

  // Loading spinner component
  const LoadingSpinner = ({ size = 16, text = "" }) => (
    <div className="d-flex align-items-center justify-content-center p-3">
      <Spinner size="sm" className="me-2" />
      {text && <span className="text-muted">{text}</span>}
    </div>
  );

  // Error handling
  if (error && !currentProjectDetails) {
    return (
      <div className="content">
        <Container>
          <Row>
            <Col md="12">
              <Alert color="danger" className="d-flex align-items-center">
                <AlertCircle size={16} className="me-2" />
                {error}
              </Alert>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="content">
      {showPopup.alert}
      <div className="content mt-30">
        <Container fluid>
          <Row>
            <Col md="12">
              <Card className="card-plain">
                <CardHeader>
                  <Row className="align-items-center">
                    <Col xs="8">
                      <a href="/project/projects" className="text-muted">
                        <ArrowLeft size={18} className="me-2" /> Back to Projects
                      </a>
                      <CardTitle tag="h4" className="mt-2">
                        Project Linking Dashboard
                        {isLoadingProject && <Loader size={20} className="ms-2 animate-spin" />}
                      </CardTitle>
                      <p className="category">
                        Manage connections for <strong>
                          {currentProjectDetails ? currentProjectDetails.name : "Loading..."}
                        </strong>
                      </p>
                    </Col>
                    <Col xs="4" className="text-end">
                      {currentProjectDetails && currentProjectDetails.enable_linking && (
                        <Button
                          color="danger"
                          onClick={handleToggleLinking}
                          disabled={isUpdatingLinkStatus}
                        >
                          {isUpdatingLinkStatus ? <><Spinner size="sm" /> Updating...</> : "Disable Linking"}
                        </Button>
                      )}
                    </Col>
                  </Row>
                </CardHeader>
              </Card>

              {/* Error Alert */}
              {error && (
                <Alert color="danger" className="d-flex align-items-center mb-4">
                  <AlertCircle size={16} className="me-2" />
                  {error}
                  <Button
                    color="link"
                    className="ms-auto p-0"
                    onClick={() => setError(null)}
                  >
                    <X size={16} />
                  </Button>
                </Alert>
              )}

              {/* Loading Project Details */}
              {isLoadingProject ? (
                <LoadingSpinner text="Loading project details..." />
              ) : currentProjectDetails ? (
                !currentProjectDetails.enable_linking ? (
                  <Row className="justify-content-center mt-4">
                    <Col md="8">
                      <Card className="text-center p-4 shadow-sm">
                        <CardBody>
                          <Unlink size={48} className="text-muted mb-3" />
                          <CardTitle tag="h5">Project Linking is Disabled</CardTitle>
                          <CardText className="text-muted">
                            Enable project linking to connect this project with others. This allows you to share and reference content across your projects, creating a more integrated experience.
                          </CardText>
                          <Button
                            color="primary"
                            onClick={handleToggleLinking}
                            disabled={isUpdatingLinkStatus}
                            style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
                          >
                            {isUpdatingLinkStatus ? <><Spinner size="sm" /> Enabling...</> : "Enable Linking Now"}
                          </Button>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                ) : (
                  <>
                    {/* Filter and Search Section */}
                    <Card className="card-signup text-center mb-4">
                      <CardHeader>

                        <Form id="projectfilters" className="form" onSubmit={handleSubmit}>
                          <Row>
                            <Col sm="4">
                              <FormGroup>
                                <Label for="publisherids">Publisher</Label>
                                <Select
                                  className="react-select primary"
                                  classNamePrefix="react-select"
                                  placeholder="Select Publishers..."
                                  name="publisherids"
                                  id="publisherids"
                                  isMulti={true}
                                  isLoading={isLoadingProject}
                                  options={allPublishers}
                                  value={allPublishers.filter(option =>
                                    formdetails.publisherids.includes(option.value)
                                  )}
                                  onChange={handlePublisherChange}
                                  isDisabled={true}
                                />
                              </FormGroup>
                            </Col>
                            <Col sm="4">
                              <FormGroup>
                                <Label for="projectname">Project Name</Label>
                                <Input
                                  type="text"
                                  name="projectname"
                                  id="projectname"
                                  value={formdetails.projectname}
                                  onChange={handleChange}
                                  placeholder="Project Name..."
                                />
                              </FormGroup>
                            </Col>
                            <Col sm="4">
                              <FormGroup>
                                <Label for="projectslug">Project Slug</Label>
                                <Input
                                  type="text"
                                  name="projectslug"
                                  id="projectslug"
                                  value={formdetails.projectslug}
                                  onChange={handleChange}
                                  placeholder="Project Slug..."
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row>
                            <Col md="12">
                              <div className="d-flex justify-content-center">
                                <Button
                                  className="btn-round me-2"
                                  color="info"
                                  type="submit"
                                  disabled={isLoadingProjects}
                                >
                                  {isLoadingProjects ? (
                                    <><Spinner size="sm" className="me-2" /> Searching...</>
                                  ) : (
                                    <><Search size={14} className="me-2" /> Search</>
                                  )}
                                </Button>
                                <Button
                                  className="btn-round"
                                  color="warning"
                                  type="button"
                                  onClick={resetElements}
                                  disabled={isLoadingProjects}
                                >
                                  Reset
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </Form>
                      </CardHeader>
                    </Card>

                    <Row>
                      {/* Available Projects */}
                      <Col md="6">
                        <Card className="shadow-sm">
                          <CardHeader>
                            <CardTitle tag="h5">
                              Available Projects
                              {isLoadingProjects && <Spinner size="sm" className="ms-2" />}
                            </CardTitle>
                            <CardText className="text-muted small">
                              Link related projects to create cross-references and improve content discovery.
                            </CardText>
                            <CardText className="text-muted">
                              Select projects to link with {currentProjectDetails.name}
                            </CardText>
                          </CardHeader>
                          <CardBody>

                            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                              {isLoadingProjects ? (
                                <LoadingSpinner text="Loading projects..." />
                              ) : projects.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                  <p>No available projects to link</p>
                                  <small>All projects are either already linked or filtered out</small>
                                </div>
                              ) : (
                                projects.map((project) => (
                                  <div
                                    key={project.id}
                                    className="border rounded p-3 d-flex justify-content-between align-items-start mb-2"
                                  >
                                    <div className="flex-grow-1">
                                      <h6 className="mb-1">
                                        {project.name}{" "}
                                        <Badge color={project.status === "active" ? "primary" : "secondary"}>
                                          {project.status}
                                        </Badge>
                                      </h6>
                                      <p className="mb-0 text-muted small">
                                        Created: {project.created_at ? <Moment utc format="DD MMM YYYY">{project.created_at}</Moment> : "N/A"}
                                      </p>
                                    </div>
                                    <Button
                                      color="primary"
                                      style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
                                      size="sm"
                                      onClick={() => handleLinkProject(project.id, project.name)}
                                      disabled={isLinkingProjects}
                                    >
                                      <Link2 size={14} className="me-1" /> Link
                                    </Button>
                                  </div>
                                ))
                              )}
                            </div>

                            {/* Pagination */}
                            {totalProjects > 0 && !isLoadingProjects && (
                              <div className="d-flex justify-content-between align-items-center mt-3">
                                <div>
                                  <b>{firstRecord} - {lastRecord} of {totalProjects}</b>
                                </div>
                                <Pagination
                                  activePage={activePage}
                                  itemsCountPerPage={records}
                                  totalItemsCount={totalProjects}
                                  pageRangeDisplayed={3}
                                  onChange={handlePageChange}
                                  itemClass="page-item"
                                  linkClass="page-link"
                                />
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </Col>

                      {/* Linked Projects */}
                      <Col md="6">
                        <Card className="shadow-sm">
                          <CardHeader>
                            <CardTitle tag="h5">
                              <Link2 className="me-2" size={18} />
                              Linked Projects ({linkedProjects.length})
                            </CardTitle>
                            <CardText className="text-muted">
                              Projects currently linked to {currentProjectDetails.name}
                            </CardText>
                          </CardHeader>
                          <FormGroup className="mx-3 my-3">
                            <InputGroup>
                              <Input
                                type="search"
                                bsSize="sm"
                                value={linkedProjectSearch}
                                onChange={handleLinkedProjectSearchChange}
                                placeholder="🔍 Search linked projects..."
                                style={{
                                  WebkitAppearance: 'none', // hide default Chrome/Edge search clear
                                  appearance: 'none',
                                  paddingRight: '2rem', 
                                }}
                                className="no-native-clear"

                              />
                              {/* {linkedProjectSearch && (
                                <InputGroupAddon addonType="append">
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    onClick={handleClearLinkedProjectSearch}
                                    title="Clear search"
                                    className="d-flex align-items-center"
                                  >
                                    <X size={14} />
                                  </Button>
                                </InputGroupAddon>
                              )} */}
                            </InputGroup>
                          </FormGroup>

                          <CardBody>
                            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                              {linkedProjects.length === 0 ? (
                                <div className="text-center text-muted py-4">
                                  <Link2 size={36} className="opacity-50 mb-2" />
                                  <p className="mb-1">No linked projects yet</p>
                                  <small>Select projects from the available list to create links</small>
                                </div>
                              ) : (
                                currentLinkedProjects.map((project) => (
                                  <div
                                    key={project._id}
                                    className="border rounded p-3 d-flex justify-content-between align-items-start mb-2"
                                  >
                                    <div className="flex-grow-1">
                                      <h6 className="mb-1">
                                        {project.name}{" "}
                                        <Badge color={project.status === "active" ? "primary" : "secondary"}>
                                          {project.status}
                                        </Badge>
                                      </h6>
                                      <p className="mb-0 text-muted small">
                                        Created: {project.created_at ? <Moment utc format="DD MMM YYYY">{project.created_at}</Moment> : "N/A"}
                                      </p>
                                    </div>
                                    <Button
                                      color="outline-danger"
                                      size="sm"
                                      onClick={() => handleUnlinkProject(project._id)}
                                    >
                                      <Unlink size={14} className="me-1" /> Unlink
                                    </Button>
                                  </div>
                                ))
                              )}
                            </div>
                            {filteredLinkedProjects.length > 0 && (
                              <div className="d-flex justify-content-between align-items-center mt-3">
                                <div>
                                  <b>{indexOfFirstLinked + 1} - {Math.min(indexOfLastLinked, filteredLinkedProjects.length)} of {filteredLinkedProjects.length}</b>
                                </div>
                                <Pagination
                                  activePage={linkedActivePage}
                                  itemsCountPerPage={linkedRecords}
                                  totalItemsCount={filteredLinkedProjects.length}
                                  pageRangeDisplayed={3}
                                  onChange={handleLinkedPageChange}
                                  itemClass="page-item"
                                  linkClass="page-link"
                                />
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </>
                )
              ) : null}
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}